<template>
  <v-container fluid>
    <v-layout row wrap>

      <v-flex xs12>
        <v-layout row wrap mb-2 align-center>
          <v-flex xs12 sm4>
            <v-text-field
              v-model="searchQuery"
              append-icon="fa-search"
              label="Search Batch ID"
              single-line
              hide-details
              @input="onSearchInput"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm2>
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              clearable
              hide-details
              @change="onFilterChange"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm2>
            <v-select
              v-model="typeFilter"
              :items="typeOptions"
              label="Type"
              clearable
              hide-details
              @change="onFilterChange"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm4 class="text-xs-right">
            <v-btn color="primary" @click="exportData">
              <v-icon left>fa-download</v-icon>
              Export CSV
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12>
        <v-layout row wrap mb-2 class="mt-3">
          <v-flex xs12>
            <h3>Funding Batches</h3>
          </v-flex>
        </v-layout>
        <skeleton-table v-if="loading" :row-count="15" :column-count="8" show-header></skeleton-table>
        <v-data-table
          v-else
          :headers="batchHeaders"
          :items="allBatches"
          :hide-actions="true"
          :expand="expand"
          item-key="uniqueKey"
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr @click="toggleExpand(props)" style="cursor: pointer;">
              <td class="text-xs-center pa-2">
                <v-progress-circular
                  v-if="loadingBatchId === props.item.uniqueKey"
                  indeterminate
                  color="primary"
                  :size="16"
                  :width="2"
                ></v-progress-circular>
                <v-icon v-else small>{{ props.expanded ? 'fa-chevron-down' : 'fa-chevron-right' }}</v-icon>
              </td>
              <td class="text-xs-left pa-2">{{ props.item.batchId }}</td>
              <td class="text-xs-left pa-2">{{ props.item.merchantName }}</td>
              <td class="text-xs-right pa-2">{{ formatCurrency(props.item.netAmount) }}</td>
              <td class="text-xs-right pa-2">{{ props.item.transactionCount }}</td>
              <td class="text-xs-center pa-2">{{ props.item.status }}</td>
              <td class="text-xs-center pa-2">{{ props.item.systemType.toUpperCase() }}</td>
              <td class="text-xs-center pa-2">{{ formatDate(props.item.date) }}</td>
            </tr>
          </template>
          <template v-slot:expand="props">
            <v-card flat class="grey lighten-4 ma-2">
              <v-card-title class="subheading pa-2">Transaction Details</v-card-title>
              <v-card-text class="pa-2">
                <v-layout v-if="loadingBatchId === props.item.uniqueKey" row wrap justify-center align-center style="min-height: 200px;">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="50"
                  ></v-progress-circular>
                  <div class="ml-3 grey--text">Loading transactions...</div>
                </v-layout>
                <div v-else>
                <v-layout row wrap mb-2 align-center>
                  <v-flex xs12 sm3>
                    <v-text-field
                      v-model="nestedFilters.transactionId"
                      label="Search Transaction ID"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm2 v-if="props.item.systemType === 'card'">
                    <v-select
                      v-model="nestedFilters.cardBrand"
                      :items="cardBrandOptions"
                      label="Card Brand"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12 sm2>
                    <v-text-field
                      v-model="nestedFilters.amountMin"
                      label="Min Amount"
                      type="number"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                      prefix="$"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm2>
                    <v-text-field
                      v-model="nestedFilters.amountMax"
                      label="Max Amount"
                      type="number"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                      prefix="$"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm2>
                    <v-text-field
                      v-model="nestedFilters.dateFrom"
                      label="From Date"
                      type="date"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm2>
                    <v-text-field
                      v-model="nestedFilters.dateTo"
                      label="To Date"
                      type="date"
                      clearable
                      hide-details
                      dense
                      class="mr-2"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm1>
                    <v-btn small flat @click="clearNestedFilters" class="ma-0">
                      Clear
                    </v-btn>
                  </v-flex>
                </v-layout>
                <v-layout row wrap mb-2 justify-end>
                  <v-btn small color="primary" @click="exportBatchTransactions(props.item)">
                    <v-icon left small>fa-download</v-icon>
                    Export Transactions CSV
                  </v-btn>
                </v-layout>
                <v-data-table
                  :headers="transactionHeaders"
                  :items="getPaginatedTransactionsForBatch(props.item)"
                  hide-actions
                  class="elevation-1"
                >
                  <template v-slot:items="txProps">
                    <td class="text-xs-left pa-2">
                      <a
                        v-if="txProps.item.trackingId"
                        href="#"
                        @click.prevent="navigateToTransaction(txProps.item.trackingId)"
                        class="primary--text"
                      >
                        {{ txProps.item.tranEventKsuid }}
                      </a>
                      <span v-else>{{ txProps.item.tranEventKsuid }}</span>
                    </td>
                    <td class="text-xs-center pa-2">{{ txProps.item.cardBrand || 'N/A' }}</td>
                    <td class="text-xs-right pa-2">{{ formatCurrency(txProps.item.transactionAmount) }}</td>
                    <td class="text-xs-right pa-2">{{ formatCurrency(txProps.item.totalFees) }}</td>
                    <td class="text-xs-right pa-2">{{ formatCurrency(txProps.item.netAmount) }}</td>
                    <td class="text-xs-center pa-2">{{ formatDate(txProps.item.tranDate) }}</td>
                  </template>
                  <template v-slot:no-data>
                    No transaction details available
                  </template>
                </v-data-table>
                <v-layout row wrap mt-2 align-center>
                  <v-flex xs12 sm6>
                    <div class="caption grey--text">
                      Showing {{ ((nestedPage - 1) * nestedPageSize) + 1 }} - {{ Math.min(nestedPage * nestedPageSize, getNestedTotalCount(props.item)) }} of {{ getNestedTotalCount(props.item) }} transactions
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="text-xs-right">
                    <v-pagination
                      v-model="nestedPage"
                      :length="getNestedTotalPages(props.item)"
                      :total-visible="7"
                      @input="onNestedPageChange"
                    ></v-pagination>
                  </v-flex>
                </v-layout>
                </div>
              </v-card-text>
            </v-card>
          </template>
          <template v-slot:no-data>
            No funding batches available
          </template>
        </v-data-table>

        <v-layout row wrap mt-2 align-center>
          <v-flex xs12 sm6>
            <div class="caption grey--text">
              Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} - {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} results
            </div>
          </v-flex>
          <v-flex xs12 sm6 class="text-xs-right">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              @input="onPageChange"
            ></v-pagination>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import FundingApi from '@/mixins/fundingApi'
import SkeletonTable from '@/components/common/SkeletonTable'

export default {
  components: {
    SkeletonTable
  },

  mixins: [FundingApi],

  props: {
    fundingBatches: {
      type: [Array, Object],
      default: () => []
    },
    settlementBatches: {
      type: Array,
      default: () => []
    },
    selectedMerchantKsuids: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchQuery: '',
      statusFilter: null,
      typeFilter: null,
      expand: false,
      currentPage: 1,
      itemsPerPage: 25,
      totalItems: 0,
      searchDebounceTimer: null,
      rowsPerPageItems: [10, 25, 50, 100],
      nestedPage: 1,
      nestedPageSize: 25,
      nestedFilters: {
        transactionId: '',
        cardBrand: null,
        amountMin: null,
        amountMax: null,
        dateFrom: null,
        dateTo: null
      },
      transactionCache: {},
      loadingBatchId: null,
      cardBrandOptions: [
        { text: 'All Brands', value: null },
        { text: 'VISA', value: 'VISA' },
        { text: 'Mastercard', value: 'MASTERCARD' },
        { text: 'AMEX', value: 'AMEX' },
        { text: 'Discover', value: 'DISCOVER' }
      ],
      statusOptions: [
        { text: 'All Statuses', value: null },
        { text: 'Pending', value: 'Pending' },
        { text: 'Processing', value: 'Processing' },
        { text: 'Ready', value: 'Ready' },
        { text: 'Completed', value: 'Completed' }
      ],
      typeOptions: [
        { text: 'All Types', value: null },
        { text: 'Card', value: 'card' },
        { text: 'ACH', value: 'bank' }
      ],
      batchHeaders: [
        { text: '', value: 'expand', sortable: false, width: '60px', align: 'center' },
        { text: 'Batch ID', value: 'batchId', sortable: true, width: '180px', align: 'left' },
        { text: 'Merchant Name', value: 'merchantName', sortable: true, width: '250px', align: 'left' },
        { text: 'Net Amount', value: 'netAmount', align: 'right', sortable: true, width: '140px' },
        { text: 'Txns', value: 'transactionCount', align: 'right', sortable: true, width: '80px' },
        { text: 'Status', value: 'status', sortable: true, width: '120px', align: 'center' },
        { text: 'Type', value: 'systemType', sortable: true, width: '100px', align: 'center' },
        { text: 'Date', value: 'date', sortable: true, width: '140px', align: 'center' }
      ],
      transactionHeaders: [
        { text: 'Txn Event ID', value: 'tranEventKsuid', sortable: true, width: '240px', align: 'left' },
        { text: 'Card Brand', value: 'cardBrand', sortable: true, width: '120px', align: 'center' },
        { text: 'Amount', value: 'transactionAmount', align: 'right', sortable: true, width: '130px' },
        { text: 'Fees', value: 'totalFees', align: 'right', sortable: true, width: '120px' },
        { text: 'Net', value: 'netAmount', align: 'right', sortable: true, width: '130px' },
        { text: 'Date', value: 'tranDate', sortable: true, width: '140px', align: 'center' }
      ],
      allBatches: []
    }
  },

  computed: {
    totalPages () {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    }
  },

  watch: {
    fundingBatches: {
      immediate: true,
      handler (newValue) {
        if (newValue && newValue.data && newValue.data.length > 0) {
          this.processFundingBatches()
          if (newValue.pagination) {
            this.totalItems = newValue.pagination.total_count
            this.itemsPerPage = newValue.pagination.limit
            this.currentPage = Math.floor(newValue.pagination.offset / newValue.pagination.limit) + 1
          }
        } else if (newValue && Array.isArray(newValue) && newValue.length > 0) {
          this.processFundingBatchesArray(newValue)
        } else {
          this.buildBatchList()
        }
      }
    },
    settlementBatches: {
      immediate: true,
      handler () {
        if (!this.fundingBatches || this.fundingBatches.length === 0) {
          this.buildBatchList()
        }
      }
    },
    expand: {
      handler (newValue, oldValue) {
        if (newValue === false && oldValue !== false) {
          this.resetNestedFilters()
        }
      }
    }
  },

  methods: {
    buildBatchList () {
      const batches = []

      this.settlementBatches.forEach(batch => {
        batches.push({
          uniqueKey: `${batch.settlementId}-${batch.entityDbaName || 'Unknown Merchant'}`,
          batchId: batch.settlementId,
          merchantName: batch.entityDbaName || 'Unknown Merchant',
          netAmount: this.convertSnowflakeAmount(batch.netAmount),
          transactionCount: batch.transactionCount,
          status: 'Ready',
          systemType: 'card',
          date: batch.generatedAt,
          entityKsuid: batch.entityKsuid,
          settlementId: batch.settlementId
        })
      })

      batches.sort((a, b) => new Date(b.date) - new Date(a.date))

      this.allBatches = batches
    },

    processFundingBatches () {
      const batchesData = this.fundingBatches.data || this.fundingBatches

      if (batchesData.length > 0) {
        console.log('[DataView] Sample batch data:', batchesData[0])
        console.log('[DataView] Net amount type:', typeof batchesData[0].net_amount, 'value:', batchesData[0].net_amount)
      }

      const batches = batchesData.map(batch => ({
        uniqueKey: `${batch.batch_id}-${batch.merchant_name}`,
        batchId: batch.batch_id,
        merchantName: batch.merchant_name,
        netAmount: batch.net_amount,
        transactionCount: batch.transaction_count,
        status: batch.status.charAt(0).toUpperCase() + batch.status.slice(1),
        systemType: batch.type,
        date: batch.batch_date,
        entityKsuid: batch.entity_ksuid,
        settlementId: batch.settlement_id,
        fundingId: batch.funding_id
      }))

      this.allBatches = batches
    },

    processFundingBatchesArray (batchesArray) {
      const batches = batchesArray.map(batch => ({
        uniqueKey: `${batch.batch_id}-${batch.merchant_name}`,
        batchId: batch.batch_id,
        merchantName: batch.merchant_name,
        netAmount: batch.net_amount,
        transactionCount: batch.transaction_count,
        status: batch.status.charAt(0).toUpperCase() + batch.status.slice(1),
        systemType: batch.type,
        date: batch.batch_date,
        entityKsuid: batch.entity_ksuid,
        settlementId: batch.settlement_id,
        fundingId: batch.funding_id
      }))

      this.allBatches = batches
    },

    emitFilterChange (resetPage = true) {
      if (resetPage) {
        this.currentPage = 1
      }

      const offset = (this.currentPage - 1) * this.itemsPerPage

      this.$emit('filter-change', {
        search: this.searchQuery || null,
        status: this.statusFilter ? [this.statusFilter] : null,
        type: this.typeFilter || null,
        limit: this.itemsPerPage,
        offset: offset,
        sortBy: 'date',
        sortOrder: 'desc'
      })
    },

    onSearchInput () {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.emitFilterChange(true)
      }, 500)
    },

    onFilterChange () {
      this.emitFilterChange(true)
    },

    onPageChange (page) {
      this.currentPage = page
      this.emitFilterChange(false)
    },

    async toggleExpand (props) {
      const isExpanding = !props.expanded
      const batch = props.item

      if (isExpanding) {
        if (!this.transactionCache[batch.uniqueKey]) {
          this.loadingBatchId = batch.uniqueKey

          try {
            const response = await this.getBatchTransactions(batch.batchId, batch.entityKsuid, batch.systemType)
            const transactionsData = response.data.data.transactions

            this.$set(this.transactionCache, batch.uniqueKey, transactionsData.map(tx => ({
              tranEventKsuid: tx.tran_event_ksuid || tx.tranEventKsuid || tx.transaction_id,
              trackingId: tx.tracking_id || tx.trackingId,
              cardBrand: tx.card_type || tx.cardBrand || null,
              transactionAmount: tx.amount || tx.transactionAmount,
              totalFees: tx.fee || tx.totalFees,
              netAmount: tx.net_amount || tx.netAmount,
              tranDate: tx.datetime || tx.tranDate
            })))
          } catch (error) {
            console.error('Error loading transactions:', error)
            this.$set(this.transactionCache, batch.uniqueKey, [])
          } finally {
            this.loadingBatchId = null
          }
        }
      }

      props.expanded = isExpanding
    },

    getTransactionsForBatch (batch) {
      return this.transactionCache[batch.uniqueKey] || []
    },

    getFilteredTransactionsForBatch (batch) {
      let transactions = this.getTransactionsForBatch(batch)

      if (this.nestedFilters.transactionId && this.nestedFilters.transactionId.trim() !== '') {
        const searchTerm = this.nestedFilters.transactionId.trim().toLowerCase()
        transactions = transactions.filter(tx =>
          tx.tranEventKsuid.toLowerCase().includes(searchTerm)
        )
      }

      if (this.nestedFilters.cardBrand && batch.systemType === 'card') {
        transactions = transactions.filter(tx =>
          tx.cardBrand === this.nestedFilters.cardBrand
        )
      }

      if (this.nestedFilters.amountMin !== null && this.nestedFilters.amountMin !== '') {
        const minAmount = parseFloat(this.nestedFilters.amountMin)
        if (!isNaN(minAmount)) {
          transactions = transactions.filter(tx =>
            tx.transactionAmount >= minAmount
          )
        }
      }

      if (this.nestedFilters.amountMax !== null && this.nestedFilters.amountMax !== '') {
        const maxAmount = parseFloat(this.nestedFilters.amountMax)
        if (!isNaN(maxAmount)) {
          transactions = transactions.filter(tx =>
            tx.transactionAmount <= maxAmount
          )
        }
      }

      if (this.nestedFilters.dateFrom) {
        const fromDate = new Date(this.nestedFilters.dateFrom)
        transactions = transactions.filter(tx => {
          const txDate = new Date(tx.tranDate)
          return txDate >= fromDate
        })
      }

      if (this.nestedFilters.dateTo) {
        const toDate = new Date(this.nestedFilters.dateTo)
        toDate.setHours(23, 59, 59, 999)
        transactions = transactions.filter(tx => {
          const txDate = new Date(tx.tranDate)
          return txDate <= toDate
        })
      }

      return transactions
    },

    getPaginatedTransactionsForBatch (batch) {
      const filteredTransactions = this.getFilteredTransactionsForBatch(batch)
      const startIndex = (this.nestedPage - 1) * this.nestedPageSize
      const endIndex = startIndex + this.nestedPageSize
      return filteredTransactions.slice(startIndex, endIndex)
    },

    getNestedTotalPages (batch) {
      const filteredTransactions = this.getFilteredTransactionsForBatch(batch)
      return Math.ceil(filteredTransactions.length / this.nestedPageSize)
    },

    getNestedTotalCount (batch) {
      const filteredTransactions = this.getFilteredTransactionsForBatch(batch)
      return filteredTransactions.length
    },

    onNestedPageChange (page) {
      this.nestedPage = page
    },

    resetNestedFilters () {
      this.nestedPage = 1
      this.nestedFilters = {
        transactionId: '',
        cardBrand: null,
        amountMin: null,
        amountMax: null,
        dateFrom: null,
        dateTo: null
      }
    },

    clearNestedFilters () {
      this.resetNestedFilters()
    },

    calculateTotalFees (tx) {
      let total = this.convertSnowflakeAmount(tx.discountFeeAmount)
      if (tx.interchangeFeeAmount) {
        total += this.convertSnowflakeAmount(tx.interchangeFeeAmount)
      }
      if (tx.processingFeeAmount) {
        total += this.convertSnowflakeAmount(tx.processingFeeAmount)
      }
      return total
    },

    /**
     * Convert Snowflake format amount string to JavaScript number
     * @param {string} amountText - Amount in Snowflake format (e.g., "12345" = $123.45)
     * @param {string} currencyExponent - Decimal places (default "2")
     * @returns {number} Converted amount as number
     */
    convertSnowflakeAmount (amountText, currencyExponent = '2') {
      if (!amountText || amountText === '0') return 0
      const amount = parseFloat(amountText)
      if (isNaN(amount)) return 0
      const exponent = parseInt(currencyExponent)
      return amount / Math.pow(10, exponent)
    },

    /**
     * Format number as USD currency
     * @param {number} value - Numeric value to format
     * @returns {string} Formatted currency string (e.g., "$123.45")
     */
    formatCurrency (value) {
      if (typeof value !== 'number' || isNaN(value)) {
        return '$0.00'
      }
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },

    getStatusColor (status) {
      const colors = {
        Pending: 'grey',
        Processing: 'warning',
        Ready: 'info',
        Completed: 'success'
      }
      return colors[status] || 'grey'
    },

    getTypeColor (type) {
      const colors = {
        card: 'primary',
        bank: 'success'
      }
      return colors[type] || 'grey'
    },

    exportData () {
      const csvData = this.generateCSV()
      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `funding_data_${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    generateCSV () {
      const headers = ['Batch ID', 'Merchant Name', 'Net Amount', 'Transactions', 'Status', 'Type', 'Date']
      const rows = this.allBatches.map(batch => [
        batch.batchId,
        batch.merchantName,
        batch.netAmount,
        batch.transactionCount,
        batch.status,
        batch.systemType,
        this.formatDate(batch.date)
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          const cellStr = String(cell)
          const escaped = cellStr.replace(/"/g, '""')
          return `"${escaped}"`
        }).join(','))
      ].join('\n')

      return csvContent
    },

    exportBatchTransactions (batch) {
      const transactions = this.getFilteredTransactionsForBatch(batch)

      if (transactions.length === 0) {
        this.$store.commit('error', 'No transactions to export')
        return
      }

      const headers = ['Transaction ID', 'Card Brand', 'Amount', 'Fees', 'Net Amount', 'Date']
      const rows = transactions.map(tx => [
        tx.tranEventKsuid,
        tx.cardBrand || 'N/A',
        tx.transactionAmount.toFixed(2),
        tx.totalFees.toFixed(2),
        tx.netAmount.toFixed(2),
        tx.tranDate
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          const cellStr = String(cell)
          const escaped = cellStr.replace(/"/g, '""')
          return `"${escaped}"`
        }).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fileName = `transactions_${batch.batchId}_${new Date().toISOString().split('T')[0]}.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      this.$store.commit('success', `Exported ${transactions.length} transactions`)
    },

    navigateToTransaction (trackingId) {
      if (!trackingId) return
      window.open(`/transactions?tracking_id=${encodeURIComponent(trackingId)}`, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
tr {
  cursor: pointer;
}

.grey.lighten-4 {
  padding: 16px;
  margin: 8px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/paynetworx/funding/DataView.vue